import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { useState, useEffect, lazy, Suspense } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { ProtectedRoute, PublicOnlyRoute } from '@/components/shared/ProtectedRoute';
import { TenantProvider } from '@/providers/TenantContextProvider';
import { useAuth } from '@/hooks/useAuth';
import { tenantService } from '@/services/tenant.service';

// Lazy-loaded pages
import { PageSpinner } from '@/components/ui/Spinner';

const CHUNK_RELOAD_KEY = 'chunk-error-reloaded';

function lazyWithRetry(factory: () => Promise<{ default: React.ComponentType }>) {
  return lazy(() =>
    factory().catch((err) => {
      // Stale service worker is serving old chunk references after a new deploy.
      // Reload once to let the new SW activate and fetch fresh chunks.
      if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
        window.location.reload();
        return new Promise<never>(() => {});
      }
      return Promise.reject(err);
    })
  );
}

function LazyPage(factory: () => Promise<{ default: React.ComponentType }>) {
  const Component = lazyWithRetry(factory);
  return (
    <Suspense fallback={<PageSpinner />}>
      <Component />
    </Suspense>
  );
}

function AuthedLayout() {
  return (
    <ProtectedRoute>
      <AppLayout>
        <Outlet />
      </AppLayout>
    </ProtectedRoute>
  );
}

function TenantLayout() {
  return (
    <TenantProvider>
      <Outlet />
    </TenantProvider>
  );
}

function GlobalRedirect() {
  const { appUser, isPlatformAdmin, loading } = useAuth();
  const [slug, setSlug] = useState<string | null>(null);

  useEffect(() => {
    if (loading) return;
    if (isPlatformAdmin()) {
      setSlug('admin');
    } else if (appUser?.role === 'CUSTOMER_VIEWER' || appUser?.role === 'CUSTOMER_MANAGER') {
      setSlug('portal');
    } else if (appUser?.tenantId) {
      tenantService.getById(appUser.tenantId).then((t) => {
        if (t) setSlug(t.slug);
      });
    }
  }, [appUser, isPlatformAdmin, loading]);

  if (loading || (!slug && appUser)) return <PageSpinner />;

  if (isPlatformAdmin()) return <Navigate to="/admin/tenants" replace />;
  if (slug === 'portal') return <Navigate to="/portal/dashboard" replace />;
  if (slug) return <Navigate to={`/${slug}/dashboard`} replace />;

  return <Navigate to="/unauthorized" replace />;
}

export const router = createBrowserRouter([
  // Public routes
  {
    path: '/login',
    element: (
      <PublicOnlyRoute>
        {LazyPage(() => import('@/features/auth/pages/LoginPage'))}
      </PublicOnlyRoute>
    ),
  },
  {
    path: '/forgot-password',
    element: LazyPage(() => import('@/features/auth/pages/ForgotPasswordPage')),
  },
  {
    path: '/unauthorized',
    element: LazyPage(() => import('@/features/auth/pages/UnauthorizedPage')),
  },

  // Public Segment Landing Pages (Phase 9 - Growth Foundation)
  {
    path: '/patio',
    element: LazyPage(async () => {
      const m = await import('@/features/landing/pages/LandingYardsPage');
      return { default: m.LandingYardsPage };
    }),
  },
  {
    path: '/locadoras',
    element: LazyPage(async () => {
      const m = await import('@/features/landing/pages/LandingRentalsPage');
      return { default: m.LandingRentalsPage };
    }),
  },
  {
    path: '/concessionarias',
    element: LazyPage(async () => {
      const m = await import('@/features/landing/pages/LandingDealershipsPage');
      return { default: m.LandingDealershipsPage };
    }),
  },
  {
    path: '/logistica',
    element: LazyPage(async () => {
      const m = await import('@/features/landing/pages/LandingLogisticsPage');
      return { default: m.LandingLogisticsPage };
    }),
  },

  // Customer Portal routes (Phase 9 - Subordinated B2B2B)
  {
    path: '/portal',
    element: (
      <ProtectedRoute>
        {LazyPage(async () => {
          const m = await import('@/features/customerPortal/components/CustomerPortalLayout');
          return { default: m.CustomerPortalLayout };
        })}
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="dashboard" replace /> },
      {
        path: 'dashboard',
        element: LazyPage(async () => {
          const m = await import('@/features/customerPortal/pages/CustomerDashboardPage');
          return { default: m.CustomerDashboardPage };
        }),
      },
      {
        path: 'vehicles',
        element: LazyPage(async () => {
          const m = await import('@/features/customerPortal/pages/CustomerVehiclesPage');
          return { default: m.CustomerVehiclesPage };
        }),
      },
      {
        path: 'vehicles/:id',
        element: LazyPage(async () => {
          const m = await import('@/features/customerPortal/pages/CustomerVehicleDetailPage');
          return { default: m.CustomerVehicleDetailPage };
        }),
      },
    ],
  },

  // Protected routes
  {
    path: '/',
    element: <AuthedLayout />,
    children: [
      { index: true, element: <GlobalRedirect /> },
      { path: 'dashboard', element: <GlobalRedirect /> },

      // Platform admin routes
      {
        path: 'admin',
        element: (
          <ProtectedRoute roles={['PLATFORM_ADMIN']}>
            <Outlet />
          </ProtectedRoute>
        ),
        children: [
          { path: 'tenants', element: LazyPage(() => import('@/features/admin/pages/TenantsPage')) },
          { path: 'tenants/:tenantId', element: LazyPage(() => import('@/features/admin/pages/TenantDetailPage')) },
          { path: 'tenants/:tenantId/ai', element: LazyPage(() => import('@/features/admin/pages/TenantAISettingsPage')) },

          { path: 'users', element: LazyPage(() => import('@/features/admin/pages/PlatformUsersPage')) },
          { path: 'ai', element: LazyPage(() => import('@/features/admin/pages/PlatformAIGovernancePage')) },
          { path: 'settings', element: LazyPage(() => import('@/features/auth/pages/SettingsPage')) },
        ],
      },

      // Tenant-scoped routes
      {
        path: ':tenantSlug',
        element: <TenantLayout />,
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },

          { path: 'dashboard', element: LazyPage(() => import('@/features/dashboard/pages/DashboardPage')) },

          { path: 'companies', element: LazyPage(() => import('@/features/admin/pages/CompaniesPage')) },
          { path: 'companies/:companyId', element: LazyPage(() => import('@/features/admin/pages/CompanyDetailPage')) },
          { path: 'parking-lots', element: LazyPage(() => import('@/features/admin/pages/ParkingLotsPage')) },
          { path: 'parking-lots/:parkingLotId', element: LazyPage(() => import('@/features/admin/pages/ParkingLotDetailPage')) },
          { path: 'users', element: LazyPage(() => import('@/features/admin/pages/UsersPage')) },

          // Inspections
          { path: 'inspections', element: LazyPage(() => import('@/features/inspection/pages/InspectionListPage')) },
          { path: 'inspections/:inspectionId', element: LazyPage(() => import('@/features/inspection/pages/InspectionDetailPage')) },
          { path: 'inspect/new', element: LazyPage(() => import('@/features/inspection/pages/InspectionNewPage')) },
          { path: 'inspect/:inspectionId', element: LazyPage(() => import('@/features/inspection/pages/InspectionFormPage')) },

          // Profile
          // Services (Fase 5)
          { path: 'services', element: LazyPage(() => import('@/features/services/pages/ServiceBoardPage')) },
          { path: 'services/:id', element: LazyPage(() => import('@/features/services/pages/ServiceOrderDetailPage')) },

          // Vehicle Intelligence (Fase 6)
          { path: 'vehicles/:vehicleId/compare', element: LazyPage(() => import('@/features/intelligence/pages/InspectionComparisonPage')) },
          { path: 'intelligence/reviews', element: LazyPage(() => import('@/features/intelligence/pages/IntelligenceReviewQueuePage')) },

          // Settings
          { path: 'settings', element: LazyPage(() => import('@/features/auth/pages/SettingsPage')) },
          { path: 'settings/ai', element: LazyPage(() => import('@/features/admin/pages/TenantAISettingsPage')) },
          { path: 'settings/vtiger', element: LazyPage(() => import('@/features/admin/pages/TenantVtigerSettingsPage')) },
          { path: 'settings/templates', element: LazyPage(() => import('@/features/admin/pages/InspectionTemplatesPage')) },
          { path: 'settings/templates/:id/versions/:versionId', element: LazyPage(() => import('@/features/admin/pages/TemplateEditorPage')) },
          { path: 'settings/api-keys', element: LazyPage(() => import('@/features/admin/pages/TenantApiKeysPage')) },

        ],
      },
    ],
  },

  // Catch-all
  { path: '*', element: <GlobalRedirect /> },
]);
